import React, { useState } from 'react';
import { Heart, Bookmark, Star, MapPin, Truck, Sparkles } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/FirebaseAuthContext';

const PinItem = ({ item, onClick, onSave, onLike }) => {
  const { themeColors } = useTheme();
  const { user, isAuthenticated } = useAuth();
  const [isHovered, setIsHovered] = useState(false);
  const [imageError, setImageError] = useState(false);

  // =============================================
  // FIXED: Safe image URL extraction
  // Handles both object format and string format
  // =============================================
  const getImageUrl = () => {
    if (!item) return null;

    // Check images array
    if (item.images && Array.isArray(item.images) && item.images.length > 0) {
      const img = item.images.find(i => i?.isPrimary) || item.images[0];

      // If it's an object with URLs
      if (img && typeof img === 'object') {
        return img.thumbnail || img.fullSize || img.url;
      }

      // If it's a direct string URL
      if (typeof img === 'string') {
        return img;
      }
    }

    // Fallback to thumbnail or image properties
    if (typeof item.thumbnail === 'string') return item.thumbnail;
    if (item.thumbnail?.url) return item.thumbnail.url;
    if (typeof item.image === 'string') return item.image;
    if (item.image?.url) return item.image.url;

    return null;
  };

  const imageUrl = getImageUrl();

  // Check if user has saved/liked this item - SAFELY
  const isSaved = user && item.saves && Array.isArray(item.saves)
    ? item.saves.some(id => id === user._id || id?.toString() === user._id)
    : false;

  const isLiked = user && item.likes && Array.isArray(item.likes)
    ? item.likes.some(id => id === user._id || id?.toString() === user._id)
    : false;

  const handleSave = (e) => {
    e.stopPropagation();
    if (onSave) onSave(item._id || item.id);
  };

  const handleLike = (e) => {
    e.stopPropagation();
    if (onLike) onLike(item._id || item.id);
  };

  // Styles
  const cardStyle = {
    position: 'relative',
    borderRadius: '16px',
    overflow: 'hidden',
    backgroundColor: themeColors?.cardBackground || '#fff',
    boxShadow: isHovered ? '0 8px 24px rgba(0,0,0,0.15)' : '0 2px 8px rgba(0,0,0,0.08)',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    transform: isHovered ? 'translateY(-4px)' : 'none',
  };

  const imageContainerStyle = {
    position: 'relative',
    width: '100%',
    paddingBottom: '100%',
    backgroundColor: themeColors?.border || '#eee',
  };

  const imageStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  };

  const placeholderStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: themeColors?.border || '#f0f0f0',
    color: themeColors?.textSecondary || '#999',
    fontSize: '48px',
  };

  const infoStyle = {
    padding: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  };

  const titleStyle = {
    fontSize: '14px',
    fontWeight: '600',
    color: themeColors?.text || '#111',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    lineHeight: 1.3,
    minHeight: '36px',
  };

  const priceStyle = {
    fontSize: '16px',
    fontWeight: '700',
    color: themeColors?.text || '#111',
  };

  const metaRowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
    color: themeColors?.textSecondary || '#767676',
    lineHeight: 1.3,
  };

  const chipStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 8px',
    borderRadius: '999px',
    backgroundColor: themeColors?.secondary || '#E8DED3',
    color: themeColors?.text || '#111',
    fontSize: '11px',
    fontWeight: '600',
    whiteSpace: 'nowrap',
  };

  const newSellerChipStyle = {
    ...chipStyle,
    backgroundColor: 'rgba(16, 185, 129, 0.12)',
    color: '#0a7a5b',
  };

  const actionsStyle = {
    position: 'absolute',
    top: '8px',
    right: '8px',
    display: 'flex',
    gap: '8px',
    opacity: isHovered ? 1 : 0,
    transition: 'opacity 0.2s',
  };

  const buttonStyle = {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255,255,255,0.9)',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  return (
    <div
      style={cardStyle}
      onClick={() => onClick && onClick(item)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div style={imageContainerStyle}>
        {imageUrl && !imageError ? (
          <img
            src={imageUrl}
            alt={item.title || 'Item'}
            style={imageStyle}
            onError={() => setImageError(true)}
            loading="lazy"
          />
        ) : (
          <div style={placeholderStyle}>📷</div>
        )}

        {isAuthenticated && (
          <div style={actionsStyle}>
            <button style={buttonStyle} onClick={handleLike}>
              <Heart
                size={16}
                color={isLiked ? '#ef4444' : '#666'}
                fill={isLiked ? '#ef4444' : 'none'}
              />
            </button>
            <button style={buttonStyle} onClick={handleSave}>
              <Bookmark
                size={16}
                color={isSaved ? '#3b82f6' : '#666'}
                fill={isSaved ? '#3b82f6' : 'none'}
              />
            </button>
          </div>
        )}

        {item.status === 'sold' && (
          <div style={{
            position: 'absolute',
            top: '8px',
            left: '8px',
            padding: '4px 8px',
            backgroundColor: 'rgba(0,0,0,0.7)',
            color: '#fff',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: '600',
          }}>
            SOLD
          </div>
        )}
      </div>

      <div style={infoStyle}>
        <div style={titleStyle}>{item.title || 'Untitled'}</div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
          {(item.size || item.ageGroup) && (
            <span style={chipStyle}>
              {item.size ? `Size ${item.size}` : item.ageGroup}
            </span>
          )}
          {item.condition && (
            <span style={{ ...chipStyle, backgroundColor: 'transparent', border: `1px solid ${themeColors?.border || '#DFD1C5'}`, fontWeight: 500 }}>
              {item.condition}
            </span>
          )}
        </div>

        <div style={priceStyle}>
          ${typeof item.price === 'number' ? item.price.toFixed(2) : '0.00'}
        </div>

        {item.user && typeof item.user === 'object' && (
          <div style={metaRowStyle}>
            <span style={{ fontWeight: 500 }}>
              {item.user.username || 'Seller'}
            </span>
            {item.user.totalRatings > 0 ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                <Star size={12} fill="#f59e0b" color="#f59e0b" />
                {(item.user.averageRating || 0).toFixed(1)}
                <span style={{ opacity: 0.7 }}>({item.user.totalRatings})</span>
              </span>
            ) : (
              <span style={newSellerChipStyle}>
                <Sparkles size={10} />
                New seller
              </span>
            )}
          </div>
        )}

        {(item.location?.city || item.shippingOptions?.shipping) && (
          <div style={metaRowStyle}>
            {item.location?.city && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                <MapPin size={11} />
                {item.location.city}
                {item.location.state ? `, ${item.location.state}` : ''}
              </span>
            )}
            {item.location?.city && item.shippingOptions?.shipping && (
              <span style={{ opacity: 0.5 }}>·</span>
            )}
            {item.shippingOptions?.shipping && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                <Truck size={11} />
                Ships
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PinItem;
